import { Dropdown } from '@/components';
import { VITE_APP_BASENAME } from '@/config';
import type { ProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { PROJECT_BASE_DIR } from '@/features/project/_utils/constants';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { initialValueOfProjectSearchForm } from '@/features/search/_models/projects-search';
import { ExportOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalysisPageSearchQueryStates } from '../../_hooks/search-query-states';
import { useAnalysisSheetType } from '../../_hooks/useAnalysisSheetType';
import styles from './AnalysisDrillThroughDropdown.module.scss';

const dropdownClass = classNames(styles.dropdown_icon);

type Props = {
  fiscalYears?: number[];
  fallbackText?: string;
  disabled?: boolean;
};

export const AnalysisDrillThroughDropdown: FC<Props> = ({
  fiscalYears,
  disabled,
  fallbackText,
}) => {
  const { searchQueryStates } = useAnalysisPageSearchQueryStates();
  const sheetType = useAnalysisSheetType();
  const currentFiscalYear = useCurrentFiscalYear();
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(
    fiscalYears?.at(-1)?.toString(),
  );
  const { t } = useTranslation();

  const initialValue = initialValueOfProjectSearchForm(
    currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
  );

  const handleDrillThrough = (fiscalYear: string) => {
    const projectSearchParams: ProjectsPageSearchQueryStates = {
      fiscalYear: fiscalYear,
      sheetType,
      q: searchQueryStates.q,
      isSearchTargetProjectName: searchQueryStates.isSearchTargetProjectName,
      cascader: searchQueryStates.cascader,
      corporateNumber: searchQueryStates.corporateNumber,
      categories: searchQueryStates.categories,
      implementationMethods: searchQueryStates.implementationMethods,
      accountingType: searchQueryStates.accountingType,
      accountingTypeIds: searchQueryStates.accountingTypeIds,
      projectNumbers: searchQueryStates.projectNumbers,
      startYear: searchQueryStates.startYear,
      endYear: searchQueryStates.endYear,
      organizationIds: searchQueryStates.organizationIds,
      otherResponsibleOrganizationIds:
        searchQueryStates.otherResponsibleOrganizationIds,
      policyIds: searchQueryStates.policyIds,
      expenditureBudgetPurposeIds:
        searchQueryStates.expenditureBudgetPurposeIds,
      page: 1,
      pageSize: 30,
      fundSheetType: null,
      skipAdministrativeReformTeamEvaluation:
        initialValue.skipAdministrativeReformTeamEvaluation,
      isPublished: null,
      inspectionByExternalExpertReviewType: null,
      isCandidateOfInspectionByExternalExpert: null,
      formulaConditions: [initialValue.formulaConditions],
      ordering: null,
      lastImplementedFiscalYearRangeMax: null,
      lastImplementedFiscalYearRangeMin: null,
      indicatorKeyword: null,
      indicatorKeywordSearchOnlyTitleAndDescription: false,
      indicatorType: null,
      indicatorTerm: null,
      indicatorDataNatureType: null,
      hasOnlyQualitativeIndicator: false,
      groupInProjectsOrdering: null,
      groupInProjectsPage: 1,
    };

    window.open(
      `${VITE_APP_BASENAME}${PROJECT_BASE_DIR}?${new URLSearchParams(
        removeNullish(projectSearchParams) as any,
      )}`,
      '_blank',
    );
  };

  return (
    <Dropdown.Button
      menu={{
        items: [...(fiscalYears ?? [])]
          .sort()
          .reverse()
          .map((fiscalYear) => ({
            key: fiscalYear,
            label: fiscalYear,
          })),
        onClick: (e) => {
          setSelectedFiscalYear(e.key);
        },
      }}
      className={dropdownClass}
      icon={selectedFiscalYear ?? fallbackText}
      onClick={() => {
        selectedFiscalYear && handleDrillThrough(selectedFiscalYear);
      }}
      disabled={disabled}
    >
      <ExportOutlined />
      {t('drill-through')}
    </Dropdown.Button>
  );
};
const removeNullish = <T extends Record<string, unknown>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined),
  );
